import { get } from 'lodash'
import { runInAction } from 'mobx'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
import rootStore from '../..'
import { responseHasError } from '../../users/actions'
import { deleteLstItem } from '../../../api/utils/api-delete'
import dayjs from 'dayjs'
import { wrapWithLoading } from '../../../config/general-actions'
import {
	getUserModifiedDetails,
	getUsersList
} from '../../../api/modules/api-user'

const uiLstUsersStore = rootStore.uiLstUsers
const usersStore = rootStore.users

// using getUserModifiedDetails api create method userHasUpdates without wrapWithLoading but should use responseHasError
/**
 * userHasUpdates
 *
 * Checks if the logged-in user's main or related data has been modified on the server.
 * It calls the `getUserModifiedDetails` API and extracts Unix timestamps for comparison.
 *
 * @async
 * @function
 * @returns {Promise<{ modified: number, modified_related: number }>} Object containing `modified` and `modified_related` Unix timestamps.
 * @throws {object} Throws the full API response in case of failure.
 */
export const userHasUpdates = async () => {
	const _response = await getUserModifiedDetails()

	const hasError = responseHasError(_response)
	if (!hasError && _response.status === 'success') {
		const { modified, modified_related } = _response
		const _modifiedTs = modified && dayjs(modified).unix()
		const _modifiedRelatedTs =
			modified_related && dayjs(modified_related).unix()

		return {
			modified: _modifiedTs,
			modified_related: _modifiedRelatedTs
		}
	} else {
		throw _response
	}
}
/**
 * @async
 * @function actLoadUsers
 * @description Asynchronously loads users from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadUsers = wrapWithLoading(
	// TODO: actLoadUsers should not load on each module.
	async (overrideParams = {}) => {
		const params = {
			show_all: uiLstUsersStore.filter.show_all,
			pg: uiLstUsersStore.pg,
			rpg: uiLstUsersStore.rpg,
			search: uiLstUsersStore.search,
			del: uiLstUsersStore.del
		}
		if (overrideParams) {
			Object.keys(overrideParams).forEach((key) => {
				params[key] = overrideParams[key]
			})
		}

		const _response = await getUsersList(params)
		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Users' }
)

/**
 * actUndoOrDeleteUser
 *
 * Either marks a user for deletion by setting a future timestamp or undoes a deletion
 * by clearing the `to_delete` field. Optionally operates in "demo" mode which updates
 * state locally without making an API call.
 *
 * @async
 * @function
 * @param {string} userId - The ID of the user to modify.
 * @param {boolean} [status=true] - Whether to delete (true) or undo delete (false).
 * @param {boolean} [demo=false] - If true, skips API call and only modifies local state.
 * @returns {Promise<boolean>} Returns true if the operation succeeds or is skipped in demo mode.
 * @throws {object} Throws the full API response if the API call fails.
 */
export const actUndoOrDeleteUser = wrapWithLoading(
	async (userId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				uiLstUsersStore.setIds(
					uiLstUsersStore.ids.filter((id) => id !== userId)
				)
				uiLstUsersStore.setNr(uiLstUsersStore.nr - 1)
			})

			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['users'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('users', {
			id: userId,
			to_delete: timestamp || false
		})
		const _storeItm = usersStore.requireUserById(userId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)

				uiLstUsersStore.setIds(
					uiLstUsersStore.ids.filter((id) => id !== userId)
				)
				uiLstUsersStore.setNr(uiLstUsersStore.nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo User' }
)

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		const clientIds = []
		const teamIds = []

		for (const _srvItm of srvItms) {
			const _storeItm = usersStore.requireUserById(_srvItm.user_id)
			_storeItm.updateFromServer(_srvItm)

			// Check roles and push user IDs accordingly
			if (_storeItm?.allowed?.roles?.team) {
				teamIds.push(_srvItm.user_id)
			}

			if (_storeItm?.allowed?.roles?.client) {
				clientIds.push(_srvItm.user_id)
			}
		}

		// Update the UI store with relevant IDs and number
		uiLstUsersStore.setIds(srvItms.map((it) => it.user_id))
		uiLstUsersStore.setClientIds(clientIds)
		uiLstUsersStore.setTeamIds(teamIds)
		uiLstUsersStore.setNr(nr)
	})
}
